import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import EventCountdown from 'components/EventCountdown/EventCountdown';
import localStorageService from 'helpers/localStorageService';
import moment from 'moment';
import styled from 'styled-components';

import { formatDateToMonthDayFormat, FULL_DATE_FORMAT_WITH_SECONDS } from '../../../../utils/dates';
import OfferDescriptionSection from './components/OfferDescriptionSection';
import PaymentActionsSection from './components/PaymentActionsSection';
import PaymentInfoSection from './components/PaymentInfoSection';


interface IMainContentModalSectionProps {
  subscriptionEndDate: string;
  isExpiredSub: boolean;
}

interface IRootProps {
  isExpandedPaymentSection: boolean;
  children?: React.ReactNode;
}


const MainContentModalSection: React.FC<IMainContentModalSectionProps> = (props) => {
  const { subscriptionEndDate, isExpiredSub } = props;

  const [isExpandedPaymentSection, setIsExpandedPaymentSection] = useState(false);
  const [isExpandedPremiumOfferSection, setIsExpandedPremiumOfferSection] = useState(false);

  const expiredSubDateLsKey = localStorageService.get('isExpiredSubDate');
  const expiredSubDatePlusThreeDays = moment(expiredSubDateLsKey, FULL_DATE_FORMAT_WITH_SECONDS).add(3, 'days').format(FULL_DATE_FORMAT_WITH_SECONDS);

  const formattedDateForOfferExpiredDate = moment(expiredSubDatePlusThreeDays, FULL_DATE_FORMAT_WITH_SECONDS);

  const offerExpiredDate = formatDateToMonthDayFormat(formattedDateForOfferExpiredDate);
  const eventEndDate = isExpiredSub ? expiredSubDatePlusThreeDays : subscriptionEndDate;

  const handleExpandPaymentSection = () => {
    setIsExpandedPaymentSection(!isExpandedPaymentSection);
    setIsExpandedPremiumOfferSection(false);
  };

  const handleExpandPremiumOfferSection = () => {
    setIsExpandedPremiumOfferSection(!isExpandedPremiumOfferSection);
    setIsExpandedPaymentSection(false);
  };

  return (
    <Root isExpandedPaymentSection={isExpandedPaymentSection} >
      <Header>
        {(!isExpandedPaymentSection || isMobile) && (
          <Title>Dear User,<br />
            we&apos;ve upgraded our payment{isMobile && <br />} system to serve you better!</Title>
        )}
        <Subtitle>Bank-level security, fast update,{isMobile && <br />} and 90% discount as our thanks.</Subtitle>
      </Header>
      <MainContent>
        <OfferDescriptionSection isExpandedPremiumOfferSection={isExpandedPremiumOfferSection} onExpandedPremiumOfferSection={handleExpandPremiumOfferSection} isExpiredSub={isExpiredSub} />
        <TimeSection>
          {isExpiredSub && <OfferEndNotification>Offer ends on {offerExpiredDate}</OfferEndNotification>}
          <EventCountdown eventDate={eventEndDate} />
        </TimeSection>
        <PaymentActionsSection isExpiredSub={isExpiredSub} />
        <PaymentInfoSectionContainer>
          <PaymentInfoSection isExpandedPaymentSection={isExpandedPaymentSection} onExpandedPaymentSection={handleExpandPaymentSection} />
        </PaymentInfoSectionContainer>
      </MainContent>
    </Root >
  );
};

export default MainContentModalSection;

const Root = styled.div<IRootProps>`
  width: 55%;
  height: 100%;
  padding: ${({ isExpandedPaymentSection }) => isExpandedPaymentSection ? '4px 32px 32px 32px' : '32px'};
  background: #1D1D1D;
  border-radius: 0 12px 12px 0;
  display: flex;
  flex-direction: column; 

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 12px;
    padding: 24px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;

  @media (max-height: 700px) {
    gap: 10px;
  }
`;

const Title = styled.span`
  font-family: Raleway;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  padding-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 24px;
    text-align: center;
  }

  @media (max-height: 700px) {
    font-size: 16px;
    line-height: 18px;
  }
`;

const Subtitle = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  color:  #A9A9A9;

  @media (max-width: 768px) {
    text-align: center;
    font-size: 14px;
    line-height: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const OfferEndNotification = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  color: #696969;

  @media (max-height: 700px) {
    font-size: 12px;
    line-height: 16px;
  }
`;


const TimeSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  align-items: center;
`;

const PaymentInfoSectionContainer = styled.div`
  margin-top: auto;
`;

